import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  type ButtonHTMLAttributes,
  forwardRef,
  isValidElement,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/utils'
import { Spinner } from './spinner'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-emphasis focus-visible:ring-offset-2 focus-visible:ring-offset-surface-page',
  {
    variants: {
      variant: {
        primary:
          'bg-text-primary text-text-inverse hover:bg-text-primary/90 active:bg-text-primary',
        secondary:
          'bg-surface-elevated text-text-primary border border-border-default hover:border-border-strong hover:bg-surface-muted',
        danger:
          'bg-status-error text-text-inverse hover:bg-status-error/90 active:bg-status-error',
        ghost: 'bg-transparent text-text-primary hover:bg-surface-muted',
        // Its height and padding are set after the size classes, below: the
        // size axis comes later in the class list and would win here.
        link: 'bg-transparent text-text-primary hover:underline underline-offset-2',
        dashed:
          'bg-transparent text-text-secondary border-2 border-dashed border-border-default hover:border-border-strong hover:text-text-primary',
      },
      size: {
        // Density-driven default — reads the active tier's control tokens
        // (comfortable == medium). Explicit small/medium/large still override.
        density:
          'h-[var(--ctl-h)] px-[var(--ctl-px)] text-[length:var(--ctl-fs)]',
        small: 'h-[30px] px-3 text-sm',
        medium: 'h-[38px] px-4 text-sm',
        large: 'h-[44px] px-5 text-base',
      },
      block: { true: 'w-full' },
      iconOnly: {
        density: 'w-[var(--ctl-h)] px-0',
        small: 'w-[30px] px-0',
        medium: 'w-[38px] px-0',
        large: 'w-[44px] px-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'density' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'iconOnly'> {
  prefixIcon?: ReactNode
  suffixIcon?: ReactNode
  loading?: boolean
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size = 'density',
      block,
      prefixIcon,
      suffixIcon,
      loading,
      disabled,
      asChild,
      children,
      ...props
    },
    ref,
  ) {
    const hasLabel = hasContent(children)
    const iconOnly = !hasLabel && (prefixIcon || suffixIcon) ? size : undefined

    const Component = asChild ? Slot : 'button'
    const content = (
      <>
        {loading ? <Spinner size={iconOnly ? 16 : 14} /> : prefixIcon}
        {hasLabel ? children : null}
        {!loading && suffixIcon}
      </>
    )

    return (
      <Component
        ref={ref}
        // `||`, not `??`: under `??` an explicit `disabled={false}` — what
        // every gated form passes — won over `loading`, so the spinner
        // rendered on a button that was still clickable.
        disabled={disabled || loading}
        className={cn(
          buttonVariants({ variant, size, block, iconOnly }),
          variant === 'link' && 'h-auto px-0',
          className,
        )}
        {...props}
      >
        {asChild && isValidElement(children) ? children : content}
      </Component>
    )
  },
)

/**
 * Whether there is anything to render as the button's label.
 *
 * An element counts wherever it sits. Counting it only at the top level made
 * `<Button><A /><B /></Button>` — an array of elements and no text — read as
 * "no label", and the button rendered empty with nothing to say so.
 */
function hasContent(children: ReactNode): boolean {
  if (children === null || children === undefined || children === false) {
    return false
  }
  if (typeof children === 'string') return children.length > 0
  if (typeof children === 'number') return true
  if (isValidElement(children)) return true
  if (Array.isArray(children)) return children.some(hasContent)
  return false
}
